import React, { useState, useEffect } from 'react';
import SafeIcon from '../../../common/SafeIcon';
import supabase from '../../../supabase/supabase';
import { Button } from '../../../components/ui/Button';
import Toast from '../../../components/ui/Toast';
import { useToast } from '../../../hooks/useToast';

export default function AdminUsers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeMenu, setActiveMenu] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteContext, setDeleteContext] = useState(null);
  
  const { toast, showToast, hideToast } = useToast();

  useEffect(() => {
    fetchCustomers();
    const closeMenu = () => setActiveMenu(null);
    window.addEventListener('click', closeMenu);
    return () => window.removeEventListener('click', closeMenu);
  }, []);

  const fetchCustomers = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('profiles_1740423033529')
      .select('*')
      .eq('role', 'customer')
      .order('created_at', { ascending: false });
    
    if (!error) setCustomers(data || []);
    setLoading(false);
  };

  const handleSuspend = async (user) => {
    const newStatus = user.is_available === false ? true : false;
    const { error } = await supabase.from('profiles_1740423033529').update({ is_available: newStatus }).eq('id', user.id);
    
    if (!error) {
      setCustomers(customers.map(c => c.id === user.id ? { ...c, is_available: newStatus } : c));
      showToast(`User status updated to ${newStatus ? 'Active' : 'Suspended'}`, 'success');
    } else {
      showToast("Error updating status: " + error.message, 'error');
    }
  };

  const executeDelete = async (id) => {
    const { error } = await supabase.rpc('delete_user_completely', { target_user_id: id });
    if (error) {
      showToast("Failed to delete user: " + error.message, 'error');
    } else {
      setCustomers(customers.filter(c => c.id !== id));
      setSelectedUser(null);
      showToast("User deleted successfully.", 'success');
    }
    setDeleteContext(null);
  };

  const filteredCustomers = customers.filter(c => 
    c.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
    c.phone?.includes(searchTerm)
  );

  return (
    <div className="animate-in fade-in duration-500 relative">
      {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}

      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-black text-brand-dark tracking-tight">Customer Directory</h1>
          <p className="text-gray-500 font-medium italic">Manage all registered app users.</p>
        </div>
      </div>

      <div className="bg-white rounded-[2rem] border border-gray-100 shadow-sm overflow-hidden min-h-[500px]">
        <div className="p-6 bg-gray-50/50 border-b border-gray-100">
          <div className="relative max-w-md">
            <SafeIcon name="Search" className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input 
              type="text" 
              placeholder="Search by name or phone..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-2xl outline-none focus:ring-4 focus:ring-brand-primary/5 focus:border-brand-primary font-bold text-sm"
            />
          </div>
        </div>

        <div className="overflow-x-auto pb-[150px]">
          {loading ? (
            <div className="p-12 text-center text-gray-400 font-bold uppercase text-[10px] tracking-widest">Fetching users...</div>
          ) : filteredCustomers.length === 0 ? (
            <div className="p-12 text-center text-gray-400 italic">No customers found.</div>
          ) : (
            <table className="w-full text-left">
              <thead>
                <tr className="text-gray-400 text-[10px] uppercase tracking-widest font-black border-b border-gray-50">
                  <th className="px-8 py-5">Customer</th>
                  <th className="px-8 py-5">Contact</th>
                  <th className="px-8 py-5">Address</th>
                  <th className="px-8 py-5">Joined</th>
                  <th className="px-8 py-5">Status</th>
                  <th className="px-8 py-5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredCustomers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50 transition-colors group">
                    <td className="px-8 py-5">
                      <div className="flex items-center gap-4">
                        <img src={user.avatar_url || `https://ui-avatars.com/api/?name=${user.full_name}&background=0047FF&color=fff`} className="w-10 h-10 rounded-full object-cover border border-gray-100" alt="" />
                        <div>
                          <p className="font-black text-brand-dark">{user.full_name}</p>
                          <p className="text-[10px] text-gray-400 font-bold uppercase tracking-tighter">{user.id.slice(0, 8)}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-8 py-5">
                      <p className="text-sm font-bold text-gray-600">{user.phone || 'N/A'}</p>
                      <p className="text-[10px] text-gray-400">{user.email}</p>
                    </td>
                    <td className="px-8 py-5">
                      <p className="text-xs font-bold text-gray-500 max-w-[150px] truncate" title={user.address}>{user.address || 'No address'}</p>
                    </td>
                    <td className="px-8 py-5 text-sm font-bold text-gray-500">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-8 py-5">
                      <span className={`px-3 py-1 text-[10px] font-black rounded-full uppercase border ${user.is_available === false ? 'bg-red-50 text-red-600 border-red-100' : 'bg-green-50 text-green-600 border-green-100'}`}>
                        {user.is_available === false ? 'Suspended' : 'Active'}
                      </span>
                    </td>
                    <td className="px-8 py-5 text-right relative">
                      <button 
                        onClick={(e) => { e.stopPropagation(); setActiveMenu(activeMenu === user.id ? null : user.id); }} 
                        className="p-2 text-gray-400 hover:text-brand-primary transition-colors"
                      >
                        <SafeIcon name="MoreVertical" />
                      </button>

                      {activeMenu === user.id && (
                        <div className="absolute right-8 top-12 w-48 bg-white shadow-2xl rounded-2xl border border-gray-100 z-50 overflow-hidden py-2 animate-in slide-in-from-top-2">
                          <button onClick={() => setSelectedUser(user)} className="w-full text-left px-4 py-3 text-xs font-black text-gray-600 hover:bg-gray-50 flex items-center gap-2">
                            <SafeIcon name="Eye" className="text-sm" /> View Profile
                          </button>
                          <button onClick={() => handleSuspend(user)} className="w-full text-left px-4 py-3 text-xs font-black text-orange-600 hover:bg-orange-50 flex items-center gap-2">
                            <SafeIcon name={user.is_available === false ? "CheckCircle" : "Slash"} className="text-sm" /> 
                            {user.is_available === false ? 'Reactivate User' : 'Suspend User'}
                          </button>
                          <div className="h-px bg-gray-100 my-1"></div>
                          <button onClick={() => setDeleteContext(user)} className="w-full text-left px-4 py-3 text-xs font-black text-red-600 hover:bg-red-50 flex items-center gap-2">
                            <SafeIcon name="Trash2" className="text-sm" /> Delete Account
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* User Profile Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-brand-dark/60 z-[100] flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-[2.5rem] w-full max-w-md p-10 shadow-2xl animate-in zoom-in duration-300">
            <div className="text-center mb-8">
              <img src={selectedUser.avatar_url || `https://ui-avatars.com/api/?name=${selectedUser.full_name}&background=0047FF&color=fff`} className="w-24 h-24 rounded-full mx-auto object-cover border-4 border-gray-50 mb-4" alt="" />
              <h2 className="text-2xl font-black text-brand-dark">{selectedUser.full_name}</h2>
              <p className="text-gray-500 font-bold text-sm mt-1">{selectedUser.email}</p>
            </div>
            
            <div className="space-y-4 bg-gray-50 p-6 rounded-2xl border border-gray-100 mb-8">
              <div>
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Phone Number</p>
                <p className="font-bold text-brand-dark">{selectedUser.phone || 'N/A'}</p>
              </div>
              <div>
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Saved Address</p>
                <p className="font-bold text-brand-dark">{selectedUser.address || 'N/A'}</p>
              </div>
              <div>
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Account Status</p>
                <p className="font-bold text-brand-dark">{selectedUser.is_available === false ? 'Suspended' : 'Active'}</p>
              </div>
            </div>

            <Button className="w-full py-4 text-sm" onClick={() => setSelectedUser(null)}>Close Profile</Button>
          </div>
        </div>
      )}

      {/* Delete User Modal */}
      {deleteContext && (
        <div className="fixed inset-0 bg-brand-dark/60 z-[200] flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-[2.5rem] w-full max-w-sm p-8 shadow-2xl text-center">
            <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon name="AlertTriangle" className="text-3xl" />
            </div>
            <h3 className="text-xl font-black text-brand-dark mb-2">Delete User?</h3>
            <p className="text-gray-500 text-sm mb-6">Are you sure you want to completely delete <strong>{deleteContext.full_name}</strong>? This action cannot be undone.</p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setDeleteContext(null)}>Cancel</Button>
              <Button variant="danger" className="flex-1 bg-red-600 hover:bg-red-700 text-white" onClick={() => executeDelete(deleteContext.id)}>Delete</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}